import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import { createChatSession, readProject } from '@api/narrative-projects';
import { normalizeTopicIds } from '@helpers';
import { Layout } from '@utilities';

import ModalBuildNarrative from '../Narratives/components/ModalBuildNarrative';
import NarrativeFlowView from '../../components/NarrativeFlowView';
import Page from '../../layouts/Page';

export const Narrative = ({ id, publicToken }) => {
  const navigate = useNavigate();
  // if flowId is not passed as a prop, look for param in the URL params
  let { flow_id: flowId } = useParams();
  const { permissions = {} } = useSelector((state) => state?.user?.data) || {};
  const { canAccessBuildYourOwn, canAccessChat } = permissions;
  const shouldShowExcludedTopics = !(canAccessBuildYourOwn || canAccessChat);

  flowId = Number(id || flowId);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const handleReprompt = (prompt = null) => {
    setSelectedPrompt(prompt);
    setIsModalOpen(true);
  };
  const prepareSharedNarrativeBuild = async () => {
    const chatSessionId = await createChatSession();
    const project = await readProject({ chatSessionId });

    if (!project?.id) {
      throw new Error('Project provisioning did not return a project identifier.');
    }

    return {
      chatSessionId,
      projectId: project.id,
    };
  };
  const handleSharedNarrativeBuilt = (nextFlowId, buildContext) => {
    setIsModalOpen(false);
    navigate(
      `/dashboard/narratives?sessionId=${buildContext.chatSessionId}&narrativeId=${nextFlowId}`
    );
  };
  return (
    <NarrativeFlowView
      flowId={flowId}
      onReprompt={handleReprompt}
      publicToken={publicToken}
      renderAfterContent={({ flow, narrativeRoute }) => {
        const flowTopicIds = normalizeTopicIds(flow?.topic_ids);
        const topicIds =
          flowTopicIds.length > 0
            ? flowTopicIds
            : normalizeTopicIds(flow?.topics?.map((topic) => topic?.topicId));

        const shouldCreateProjectForReprompt =
          flow?.narrative_id === 'dynamic' && flow?.isOwner === false;

        return isModalOpen ? (
          <ModalBuildNarrative
            flowId={flow?.id}
            onNarrativeBuilt={
              shouldCreateProjectForReprompt ? handleSharedNarrativeBuilt : undefined
            }
            prepareNarrativeBuild={
              shouldCreateProjectForReprompt ? prepareSharedNarrativeBuild : undefined
            }
            projectId={flow?.projectId ?? null}
            route={narrativeRoute}
            selectedPrompt={selectedPrompt}
            setIsModalOpen={setIsModalOpen}
            topicIds={topicIds}
          />
        ) : null;
      }}
      shouldShowExcludedTopics={shouldShowExcludedTopics}
    />
  );
};

Narrative.propTypes = {
  id: PropTypes.number,
  /*
   * Optional: only required for sharing the narrative publicly
   */
  publicToken: PropTypes.string,
};

const NarrativeLayout = (props) => (
  <Page>
    <Layout.Column>
      <Narrative {...props} />
    </Layout.Column>
  </Page>
);

export default NarrativeLayout;
